/**
 * @coeldery/document-scan / frames.ts —— **瀏覽器 I/O**（video ↔ raster ↔ canvas/Blob）
 * ⚠️ 只有呢個檔用 DOM；`detect.ts`／`warp.ts` 係純函數（可以喺 Node 測）。
 * 紅線：唔改 video／唔上網；只做「抽一格 → raster」同「raster → JPEG」。
 */
import type { Raster } from './detect.ts';

/** 由 video 抽一格 → 縮到長邊 ≤ `maxDim` 嘅 RGBA raster（**偵測用**，求快）*/
export function rasterFromVideo(video: HTMLVideoElement, maxDim = 480): Raster | null {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return null;
  const scale = Math.min(1, maxDim / Math.max(vw, vh));
  return drawToRaster(video, Math.max(1, Math.round(vw * scale)), Math.max(1, Math.round(vh * scale)));
}

/** 由 video **全解像度**抽一格（**擷取用**，保持細節）*/
export function fullRasterFromVideo(video: HTMLVideoElement): Raster | null {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return null;
  return drawToRaster(video, vw, vh);
}

function drawToRaster(src: CanvasImageSource, width: number, height: number): Raster | null {
  const c = document.createElement('canvas');
  c.width = width; c.height = height;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(src, 0, 0, width, height);
  const img = ctx.getImageData(0, 0, width, height);
  return { data: img.data, width, height };
}

/** RGBA raster → JPEG data URL（用嚟喺「拉四角」畫面顯示）*/
export function rasterToDataUrl(raster: Raster, quality = 0.8): string {
  const c = document.createElement('canvas');
  c.width = raster.width; c.height = raster.height;
  const ctx = c.getContext('2d');
  if (!ctx) return '';
  // ⚠️ TS 7 對 Uint8ClampedArray 泛型好嚴 → 用「明確 ArrayBuffer」版本
  const bytes = new Uint8ClampedArray(new ArrayBuffer(raster.width * raster.height * 4));
  bytes.set(raster.data);
  ctx.putImageData(new ImageData(bytes, raster.width, raster.height), 0, 0);
  return c.toDataURL('image/jpeg', quality);
}

/** RGBA raster → JPEG Blob（`data` 可為 Uint8Array 或 Uint8ClampedArray）*/
export function rasterToJpeg(raster: Raster, quality = 0.85): Promise<Blob | null> {
  const c = document.createElement('canvas');
  c.width = raster.width; c.height = raster.height;
  const ctx = c.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  // ⚠️ TS 5.7+／7 對 Uint8ClampedArray 泛型好嚴 → 一定要用「明確 ArrayBuffer」版本（同 passcode-hash 一樣理由）
  const bytes = new Uint8ClampedArray(new ArrayBuffer(raster.width * raster.height * 4));
  bytes.set(raster.data);
  ctx.putImageData(new ImageData(bytes, raster.width, raster.height), 0, 0);
  return new Promise((resolve) => c.toBlob((b) => resolve(b), 'image/jpeg', quality));
}
